<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useStore } from 'vuex'
import DashboardModal from '@/components/dashboard/ui/DashboardModal.vue'
import { useDeleteLinkedAccountModal } from '@/composables/useDeleteLinkedAccountModal'

const store = useStore()
const { isOpen, closeDeleteLinkedAccountModal } = useDeleteLinkedAccountModal()

const isSubmitting = ref(false)
const confirmButtonRef = ref<HTMLButtonElement | null>(null)

const accountToBeDeleted = computed(
  () => store.state.accounts.accountToBeDeleted as string
)

const accountToBeDeletedMeta = computed(
  () => store.state.accounts.accountToBeDeletedMeta as { type?: string; ref?: string }
)

const isEmailRemoval = computed(
  () => accountToBeDeletedMeta.value.type === 'email address'
)

const accountLabel = computed(() => {
  const { type, ref } = accountToBeDeletedMeta.value
  if (!type && !ref) return accountToBeDeleted.value
  return `${type ?? ''} ${ref ?? ''}`.trim()
})

const emailAddress = computed(
  () => accountToBeDeletedMeta.value.ref ?? accountToBeDeleted.value
)

const describedByIds = computed(() => {
  const ids = ['delete-linked-account-intro']
  if (isEmailRemoval.value) {
    ids.push('delete-linked-account-email-note')
  }
  return ids.join(' ')
})

watch(isOpen, async (open) => {
  if (!open) {
    isSubmitting.value = false
    store.dispatch('accounts/clearAccountToBeDeleted')
    return
  }

  await nextTick()
  confirmButtonRef.value?.focus()
})

const requestClose = () => {
  if (isSubmitting.value) return
  closeDeleteLinkedAccountModal()
}

const removeAccount = async () => {
  if (!accountToBeDeleted.value) return

  isSubmitting.value = true
  try {
    await store.dispatch('accounts/deleteAccount', accountToBeDeleted.value)
    closeDeleteLinkedAccountModal()
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <DashboardModal
    :is-open="isOpen && Boolean(accountToBeDeleted)"
    modal-class="sa-manage-auth-modal sa-delete-linked-account-modal"
    :aria-labelledby="isEmailRemoval ? 'delete-linked-email-title' : 'delete-linked-account-title'"
    :aria-describedby="describedByIds"
    :close-disabled="isSubmitting"
    @close="requestClose"
  >
    <div
      class="sa-modal-icon sa-manage-auth-modal__icon sa-manage-auth-modal__icon--warning"
      aria-hidden="true"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path
          d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>

    <h2
      :id="isEmailRemoval ? 'delete-linked-email-title' : 'delete-linked-account-title'"
      class="sa-manage-auth-modal__title"
    >
      {{
        isEmailRemoval
          ? $t('Dashboard.Status.DeleteLinkedModal.TitleEmail')
          : $t('Dashboard.Status.DeleteLinkedModal.Title')
      }}
    </h2>

    <p id="delete-linked-account-intro" class="sa-manage-auth-modal__intro">
      <i18n-t
        v-if="isEmailRemoval"
        keypath="Dashboard.Status.DeleteLinkedModal.IntroEmail"
        scope="global"
      >
        <template #email>
          <strong>{{ emailAddress }}</strong>
        </template>
      </i18n-t>
      <i18n-t v-else keypath="Dashboard.Status.DeleteLinkedModal.Intro" scope="global">
        <template #account>
          <strong>{{ accountLabel }}</strong>
        </template>
      </i18n-t>
    </p>

    <p
      v-if="isEmailRemoval"
      id="delete-linked-account-email-note"
      class="sa-delete-linked-account-modal__note"
      role="note"
    >
      {{ $t('Dashboard.Status.DeleteLinkedModal.EmailMatchingNote') }}
    </p>

    <template #footer>
      <div class="sa-modal-actions sa-manage-auth-modal__actions">
        <button
          type="button"
          class="sa-btn-secondary"
          :disabled="isSubmitting"
          @click="requestClose"
        >
          {{ $t('Dashboard.Status.DeleteLinkedModal.Cancel') }}
        </button>
        <button
          ref="confirmButtonRef"
          type="button"
          class="sa-btn-secondary sa-btn-unauthorise"
          :disabled="isSubmitting"
          :aria-busy="isSubmitting"
          @click="removeAccount"
        >
          <span v-if="isSubmitting" class="spinner sa-btn-spinner" aria-hidden="true" />
          <span>{{ $t('Dashboard.Status.DeleteLinkedModal.Confirm') }}</span>
        </button>
      </div>
    </template>
  </DashboardModal>
</template>
